import { makeVar, useReactiveVar } from '@apollo/client';
import {
  FontIcon,
  IPanelProps,
  mergeStyles,
  Panel,
  PanelType,
  useTheme,
} from '@fluentui/react';
import { DRAWER_CLOSE_WIDTH, LISTVIEW_MODAL_HEIGHT } from 'common/constants';
import React, { useCallback, useEffect, useRef } from 'react';
import { togglePanelSize } from '../PanelHeader';
import { setActiveOrderIndex } from 'utility/cache/ui';

type DraggablePanelProps = Omit<IPanelProps, 'type' | 'customWidth'> & {
  initialWidth: number;
  minWidth?: number;
  isModalView?: boolean;
};

export const setPanelWidth = makeVar<number | undefined>(undefined);
const DraggablePanel: React.FC<DraggablePanelProps> = ({
  children,
  initialWidth,
  minWidth = 300,
  layerProps,
  onDismiss,
  isModalView = false,
  ...props
}) => {
  const renderRef = useRef(true);
  const panelWidth = useReactiveVar(setPanelWidth);
  const isExpanded = useReactiveVar(togglePanelSize);
  const activeRef = useRef<boolean>(false);
  const theme = useTheme();
  const centeredPanelClass = mergeStyles({
    selectors: {
      '.ms-Panel-main': {
        position: 'fixed',
        top: `calc(50% - ${LISTVIEW_MODAL_HEIGHT / 2}px)`,
        left: `calc(50% - ${initialWidth / 2}px)`,
        height: 'auto',
        maxHeight: '80vh',
      },
    },
  });

  const onMouseUp = useCallback((e: MouseEvent) => {
    activeRef.current = false;
  }, []);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (activeRef.current) {
        const newWidth = window.innerWidth - e.clientX;
        const value = Math.max(
          minWidth,
          Math.min(newWidth, window.innerWidth - DRAWER_CLOSE_WIDTH)
        );
        setPanelWidth(value);
        activeRef.current = true;

        e.preventDefault();
      }
    },
    [minWidth, panelWidth]
  );

  const onMouseDown = useCallback((e: MouseEvent) => {
    if (activeRef.current) e.preventDefault();
  }, []);

  const removeEventListeners = useCallback(() => {
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mousedown', onMouseDown);
  }, [onMouseUp, onMouseMove]);

  useEffect(() => {
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);

    return () => {
      removeEventListeners();
    };
  }, [onMouseUp, onMouseMove, removeEventListeners]);

  useEffect(() => {
    setActiveOrderIndex(new Map());
  }, [children]);

  useEffect(() => {
    setPanelWidth(
      isExpanded
        ? window.innerWidth - DRAWER_CLOSE_WIDTH
        : renderRef.current
          ? panelWidth
            ? panelWidth
            : minWidth
          : initialWidth
    );
    renderRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  return (
    <Panel
      {...props}
      onDismiss={(e) => {
        removeEventListeners();
        onDismiss?.(e);
      }}
      isBlocking={isModalView}
      type={PanelType.custom}
      customWidth={`${panelWidth}px`}
      layerProps={{ eventBubblingEnabled: true, ...layerProps }}
      className={isModalView ? centeredPanelClass : undefined}
      styles={{
        ...props.styles,
        overlay: isModalView
          ? {
              backgroundColor: theme.isInverted
                ? 'rgba(0,0,0,0.6)'
                : 'rgba(0,0,0,0.4)',
            }
          : {},
      }}
    >
      <div
        style={{
          position: 'fixed',
          height: '100%',
          width: 10,
          right: (panelWidth || 0) - 10,
          top: 0,
          cursor: 'ew-resize',
          backgroundColor: theme.palette.neutralLighter,
          display: 'flex',
          alignItems: 'center',
        }}
        onMouseDown={(e) => {
          activeRef.current = true;
        }}
      >
        <FontIcon
          iconName="GripperBarVertical"
          style={{ marginLeft: '-2px', color: theme.palette.neutralQuaternary }}
        />
      </div>

      {children}
    </Panel>
  );
};

export default DraggablePanel;
